import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useCallback, useRef } from 'react';
import { useBriefState } from '../lib/brief-state';
import { useRefine } from '../lib/use-refine';
import type { BriefSection, ClusterId } from '../types/canvas';
import { SECTION_MANIFEST } from '../types/canvas';
import {
  BriefHeader,
  BriefHero,
  ClusterSection,
  HeroCard,
  EntityCard,
  ProseCard,
  ListCard,
  VoiceCard,
  ConstraintsCard,
  BeatsCard,
  VisualCard,
  WebCard,
  SkeletonCard,
} from '../components/canvas';
import { RefineDrawer } from '../components/canvas/RefineDrawer';
import { CascadeBadge } from '../components/canvas/CascadeBadge';

const CLUSTER_LABELS: Record<ClusterId, string> = {
  'story-core': 'Story Core',
  'world': 'World',
  'cast': 'Cast',
  'engine': 'Engine',
  'craft': 'Craft',
  'meta': 'Meta',
};

const CLUSTER_ORDER: ClusterId[] = ['story-core', 'world', 'cast', 'engine', 'craft', 'meta'];

export default function BriefCanvas() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { session, loading, streamStatus, load, patchSection, patchApproval } = useBriefState();
  const refine = useRefine();

  const arrivedRef = useRef<Set<string>>(new Set());
  const prevSectionsRef = useRef<number>(0);

  useEffect(() => {
    const streaming = streamStatus === 'streaming' || streamStatus === 'connecting';
    if (id && id !== 'new-session' && (!session || session.sessionId !== id) && !streaming) {
      load(id);
    }
  }, [id, session, load, streamStatus]);

  useEffect(() => {
    if (id === 'new-session' && session?.sessionId) {
      navigate(`/brief/${session.sessionId}`, { replace: true });
    }
  }, [id, session?.sessionId, navigate]);

  const handleSectionUpdate = useCallback((updated: BriefSection) => {
    patchSection(updated);
  }, [patchSection]);

  const handleApproval = useCallback((sectionId: string, approvalCount: number) => {
    patchApproval(sectionId, approvalCount);
    refine.dismissWarning(sectionId);
  }, [patchApproval, refine]);

  useEffect(() => {
    refine.onSectionUpdate(handleSectionUpdate);
    refine.onApproval(handleApproval);
  }, [refine, handleSectionUpdate, handleApproval]);

  const isStreaming = streamStatus === 'streaming' || streamStatus === 'connecting';
  const sections = session?.sections ?? [];

  // Track newly-arrived sections for entry animation
  if (sections.length > prevSectionsRef.current) {
    for (let i = prevSectionsRef.current; i < sections.length; i++) {
      arrivedRef.current.add(sections[i].id);
    }
    prevSectionsRef.current = sections.length;
  }

  // Full-page loading state
  if (loading && !session) {
    return (
      <div className="min-h-screen bg-background">
        <BriefHeader sections={[]} streamStatus="connecting" />
        <div className="max-w-[1080px] mx-auto px-5 sm:px-8 pt-9 sm:pt-10">
          <div className="h-6 w-24 bg-border/40 rounded animate-pulse mb-2" />
          <div className="h-8 w-64 bg-border/60 rounded animate-pulse mb-5" />
        </div>
        <div className="max-w-[1080px] mx-auto px-5 sm:px-8 pt-7 pb-20">
          {CLUSTER_ORDER.map((clusterId) => {
            const manifestForCluster = SECTION_MANIFEST.filter((m) => m.clusterId === clusterId);
            if (manifestForCluster.length === 0) return null;
            return (
              <ClusterSection key={clusterId} label={CLUSTER_LABELS[clusterId]}>
                {manifestForCluster.map((m) => (
                  <SkeletonCard key={m.id} title={m.title} fullWidth={m.fullWidth} />
                ))}
              </ClusterSection>
            );
          })}
        </div>
      </div>
    );
  }

  if (!session || session.sessionId !== id) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading brief...</p>
      </div>
    );
  }

  // Derive title from premise
  const premiseSection = sections.find((s) => s.id === 'premise');
  const derivedTitle = premiseSection?.data.type === 'prose'
    ? premiseSection.data.prose.split('.')[0]
    : undefined;

  function makeActions(section: BriefSection) {
    return {
      onApprove: () => refine.sendApprove(session!.sessionId, section.id),
      onRefine: () => refine.open(section.id),
      onAnother: () => refine.sendRandomize(session!.sessionId, section.id),
    };
  }

  function renderCard(section: BriefSection) {
    const { data, status, id: sectionId, title } = section;
    const turnsInfo = refine.getTurnsInfo(sectionId);
    const refineDisabled = turnsInfo != null && turnsInfo.used >= turnsInfo.max;
    const actions = { ...makeActions(section), refineDisabled };
    const isRefining = refine.activeSectionId === sectionId;
    const sectionWarnings = refine.cascadeWarnings.filter((w) => w.affectedSection === sectionId);
    const isNewArrival = isStreaming && arrivedRef.current.has(sectionId);

    let card: React.ReactNode;

    switch (data.type) {
      case 'prose':
        if (sectionId === 'premise') {
          card = <HeroCard data={data} status={status} {...actions} />;
        } else {
          card = <ProseCard title={title} data={data} status={status} {...actions} />;
        }
        break;
      case 'entity':
        card = (
          <EntityCard
            data={data}
            status={status}
            roleLabel={sectionId === 'protagonist' ? 'Protagonist' : sectionId === 'antagonist' ? 'Antagonist' : undefined}
            {...actions}
          />
        );
        break;
      case 'list':
        card = <ListCard title={title} data={data} status={status} {...actions} />;
        break;
      case 'cast':
        card = <ListCard title={title} data={data} status={status} {...actions} />;
        break;
      case 'web':
        card = <WebCard data={data} status={status} {...actions} />;
        break;
      case 'arc':
        card = <VisualCard data={data} status={status} {...actions} />;
        break;
      case 'voice':
        card = <VoiceCard data={data} status={status} {...actions} />;
        break;
      case 'constraints':
        card = <ConstraintsCard data={data} status={status} {...actions} />;
        break;
      case 'beats':
        card = <BeatsCard data={data} status={status} {...actions} />;
        break;
      default:
        card = null;
    }

    const fullWidth = data.type === 'web' || data.type === 'arc' || sectionId === 'premise';
    const isFailed = status === 'failed';

    return (
      <div
        key={sectionId}
        className={[
          'relative',
          fullWidth ? 'col-span-full' : '',
          isNewArrival ? 'animate-card-enter' : '',
          isFailed ? '[&>div:first-child]:border-dashed [&>div:first-child]:border-destructive/40' : '',
        ].filter(Boolean).join(' ')}
      >
        {sectionWarnings.length > 0 && (
          <div className="absolute top-3 right-3 z-10">
            <CascadeBadge
              warnings={sectionWarnings}
              onDismiss={() => refine.dismissWarning(sectionId)}
            />
          </div>
        )}
        {card}
        {isRefining && (
          <RefineDrawer
            sectionId={sectionId}
            sectionTitle={title}
            onSend={(msg) => refine.sendRefine(session!.sessionId, sectionId, msg)}
            onClose={refine.close}
            turns={refine.turns}
            streaming={refine.streaming}
            streamingText={refine.streamingText}
            turnsUsed={turnsInfo?.used}
            turnsMax={turnsInfo?.max}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <BriefHeader
        sections={sections}
        streamStatus={streamStatus}
        onPress={() => { /* navigate to press */ }}
      />
      <BriefHero
        title={derivedTitle}
        inputs={session.tier1Inputs}
        sections={sections}
      />

      <div className="max-w-[1080px] mx-auto px-5 sm:px-8 pt-7 pb-20">
        {CLUSTER_ORDER.map((clusterId) => {
          const clusterSections = sections.filter((s) => s.clusterId === clusterId);
          const manifestForCluster = SECTION_MANIFEST.filter((m) => m.clusterId === clusterId);

          if (!isStreaming && clusterSections.length === 0) return null;
          if (manifestForCluster.length === 0) return null;

          return (
            <ClusterSection key={clusterId} label={CLUSTER_LABELS[clusterId]}>
              {manifestForCluster.map((manifest) => {
                const section = clusterSections.find((s) => s.id === manifest.id);
                if (section) {
                  return renderCard(section);
                }
                if (isStreaming) {
                  return (
                    <SkeletonCard
                      key={manifest.id}
                      title={manifest.title}
                      fullWidth={manifest.fullWidth}
                    />
                  );
                }
                return null;
              })}
            </ClusterSection>
          );
        })}
      </div>

      <footer className="text-center max-w-[1080px] mx-auto px-5 sm:px-8 pb-12">
        <a href="#" className="text-[13px] text-muted-foreground underline underline-offset-[3px]">
          Show me a page of this book
        </a>
      </footer>
    </div>
  );
}
